/* CUSTOM: Mengenregeln (Ersatz Pareto Order Limit)
   Blockiert den Checkout-Submit der Cart-Formulare (Drawer, Cart-Page, Quick-Buy-/
   Popover-Bestätigung), solange Positionen gegen aktive Mengenregeln verstoßen.
   Das Flag data-order-limit-blocked wird serverseitig in Liquid gerendert und ist
   nach jedem Section-Rerender aktuell — dieses Script hält keinen eigenen Zustand. */
(() => {
  /* Live-Region früh anlegen, damit sie beim ersten Submit bereits im
     Accessibility-Tree hängt (defer-Script: body existiert) */
  const announcer = document.createElement('p');
  announcer.setAttribute('aria-live', 'assertive');
  announcer.className = 'sr-only';
  document.body.appendChild(announcer);

  function announce(message) {
    if (!message) return;

    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  /* CUSTOM: Altersnachweis — true, wenn ein Alters-Block aktiv UND die zugehörige Checkbox (noch)
     nicht angehakt ist. Fehlt die Checkbox unerwartet, wird sicherheitshalber weiter blockiert. */
  function isAgeVerifyBlocked(form) {
    if (form.dataset.ageVerifyBlocked !== 'true') return false;

    const checkbox = form.querySelector('input[name="age_verify_confirm"]');
    return !checkbox || !checkbox.checked;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      const ageBlocked = isAgeVerifyBlocked(form);
      if (form.dataset.orderLimitBlocked !== 'true' && !ageBlocked) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      /* Quick-Buy-/Popover-Drawer werden nach body verschoben und haben keinen
         .shopify-section-Wrapper; findet sich dort keine Warnung, wird das
         Formular selbst hervorgehoben (nie null Feedback) */
      const root = form.closest('cart-drawer, quick-buy-drawer, cart-notification-drawer, .shopify-section') || document;
      let warnings = root.querySelectorAll('.order-limit-warning, .order-limit-banner');

      if (warnings.length === 0) {
        warnings = [form];
      }

      warnings.forEach((warning) => {
        warning.classList.remove('order-limit--highlight');
        void warning.offsetWidth; /* Reflow erzwingen, damit die Animation neu startet */
        warning.classList.add('order-limit--highlight');
      });

      const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      warnings[0].scrollIntoView({ behavior: scrollBehavior, block: 'center' });
      announce(form.dataset.orderLimitMessage || form.dataset.ageVerifyMessage || '');
    },
    { capture: true }
  );
})();

/* CUSTOM: Altersnachweis — schaltet den Checkout-Button clientseitig frei/wieder gesperrt, wenn die
   Alters-Checkbox an- oder abgewählt wird. Anders als Mengenregeln/Presale kann dieser Blocker OHNE
   Cart-Mutation aufgehoben werden, Liquid kann den Haken beim Rendern also nicht kennen — deshalb ein
   eigener change-Listener statt reiner Server-Neuberechnung. Bewusst OHNE capture-Phase (anders als die
   beiden Listener oben/unten): die Checkbox hat keinen konkurrierenden Handler, den es zu überholen gilt. */
(() => {
  document.addEventListener('change', (event) => {
    const checkbox = event.target;

    if (!(checkbox instanceof HTMLInputElement) || checkbox.name !== 'age_verify_confirm') return;

    const form = checkbox.closest('form');
    if (!form) return;

    const button = form.querySelector('button[type="submit"][name="checkout"]');
    if (!button) return;

    const shouldDisable = form.dataset.orderLimitBlocked === 'true' || !checkbox.checked;
    button.disabled = shouldDisable;
    button.classList.toggle('button--subdued', shouldDisable);
  });
})();

/* CUSTOM: Mengenregeln (Live-Validierung, siehe Spec §8a)
   Fängt den vom Kunden eingegebenen Rohwert ab, bevor das quantity-input-Element
   (theme.js) ihn automatisch auf min/max/step korrigiert (Capture-Phase-Listener
   auf document feuern vor Listenern, die direkt auf dem Zielelement registriert
   sind — dasselbe Prinzip wie der Checkout-Blocker oben). Zeigt bei einem
   erkannten Verstoß sofort eine Meldung im Stil der Cart-Zeilen-Warnung. */
(() => {
  function findViolation(input, rawValue) {
    const min = Number(input.min) || 1;
    const max = input.max !== '' ? Number(input.max) : null;
    const step = Number(input.step) || 1;

    if (rawValue === '') {
      return min > 1 ? { type: 'min', amount: min } : null;
    }

    const value = Number(rawValue);
    if (Number.isNaN(value)) return null;

    if (value < min) return { type: 'min', amount: min };
    if (max !== null && value > max) return { type: 'max', amount: max };
    if (step > 1 && (value - min) % step !== 0) return { type: 'multiple', amount: step };
    return null;
  }

  function resolveMessage(violation) {
    if (!violation) return '';

    /* CUSTOM: bewusst die neutralen _hint-Strings (nicht die auffordernden
       cart.order_limit.min/max/multiple der persistenten Cart-Zeilen-Warnung) — der
       Wert ist an dieser Stelle immer schon automatisch korrigiert oder der Klick war
       ein No-Op, eine Aufforderung ("bitte Menge erhöhen") wäre irreführend. */
    const strings = window.themeVariables?.strings || {};
    const templates = {
      min: strings.orderLimitMinHint,
      max: strings.orderLimitMaxHint,
      multiple: strings.orderLimitMultipleHint,
    };
    const template = templates[violation.type];

    return template ? template.replace('__N__', violation.amount) : '';
  }

  function findLiveWarning(input) {
    /* Der Platzhalter ist ein GESCHWISTER von quantity-selector/line-item-quantity
       (siehe Tasks 3/4 — beide Container sind Flex-/Grid-Boxen ohne Platz für ein
       zusätzliches Kind), daher parentElement statt direkter Kind-Suche. */
    const wrapper = input.closest('quantity-selector, line-item-quantity');
    return wrapper?.parentElement ? wrapper.parentElement.querySelector('.order-limit-live-warning') : null;
  }

  function showViolation(input, violation) {
    const warningEl = findLiveWarning(input);
    if (!warningEl) return;

    const message = resolveMessage(violation);

    warningEl.hidden = !message;
    warningEl.textContent = message;
  }

  /* CUSTOM: Mengenregeln — merkt sich einen per Stepper-Klick erkannten Grenzwert-Verstoß
     bis zum synthetischen "change"-Event, das theme.js' stepUp()/stepDown() IMMER
     auslöst (auch wenn der Schritt ein No-Op war, weil min/max bereits erreicht sind).
     Dieses change-Event läuft synchron VERSCHACHTELT innerhalb des Klick-Handlers ab
     und würde sonst die gerade gesetzte Meldung sofort wieder löschen, weil der
     unveränderte Wert für sich genommen gültig ist (4 ist kein Verstoß gegen min 4). */
  let pendingStepperViolation = null;

  function onQuantityEvent(event) {
    if (!event.target.matches?.('input[is="quantity-input"]')) return;

    const violation = findViolation(event.target, event.target.value) || pendingStepperViolation;
    pendingStepperViolation = null;
    showViolation(event.target, violation);
  }

  document.addEventListener('input', onQuantityEvent, { capture: true });
  document.addEventListener('change', onQuantityEvent, { capture: true });

  /* CUSTOM: Mengenregeln — Stepper-Buttons (nur PDP, quantity-selector) lösen weder
     "input" noch (vor dem nativen stepUp/stepDown) ein aussagekräftiges "change" aus:
     ein Klick auf "-" bei bereits erreichter Mindestmenge tut sichtbar nichts, ohne
     Erklärung. Capture-Phase VOR dem Klick-Handler des Buttons (theme.js) liest den
     Wert, bevor stepUp()/stepDown() ihn (nicht) verändert. */
  function onStepperClick(event) {
    const button = event.target.closest?.('.quantity-selector__button');
    if (!button) return;

    const wrapper = button.closest('quantity-selector');
    const input = wrapper?.querySelector('input[is="quantity-input"]');
    if (!input) return;

    const isDecrease = button === wrapper.querySelector('button:first-of-type');
    const min = Number(input.min) || 1;
    const max = input.max !== '' ? Number(input.max) : null;
    const value = Number(input.value);

    let violation = null;
    if (isDecrease && min > 1 && value <= min) {
      violation = { type: 'min', amount: min };
    } else if (!isDecrease && max !== null && value >= max) {
      violation = { type: 'max', amount: max };
    }

    pendingStepperViolation = violation;
    showViolation(input, violation);
  }

  document.addEventListener('click', onStepperClick, { capture: true });
})();
